import React, { useState, useEffect } from "react";
import { QuestionnaireQuestion } from "services/questionnaireService";
import { MdRemove, MdAdd } from "react-icons/md";

// Common props for all question type components
interface BaseQuestionProps {
  question: QuestionnaireQuestion;
  sectionId: string;
  response: any;
}

// Single Selection Question Component
interface SingleSelectionProps extends BaseQuestionProps {
  onResponseChange: (
    sectionId: string,
    questionId: string,
    value: string | number
  ) => void;
}

export const SingleSelectionQuestion: React.FC<SingleSelectionProps> = ({
  question,
  sectionId,
  response,
  onResponseChange,
}) => {
  return (
    <div className="space-y-2 md:space-y-3">
      {question.options?.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center rounded-lg border border-gray-200 p-2 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 md:p-3"
        >
          <input
            type="radio"
            name={question.id}
            value={option.value}
            checked={response === option.value}
            onChange={() => {
              // Convert to number if the option value is numeric
              const value =
                typeof option.value === "string" && !isNaN(Number(option.value))
                  ? Number(option.value)
                  : option.value;
              onResponseChange(sectionId, question.id, value);
            }}
            className="h-4 w-4 text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 md:ml-3 md:text-base">
            {option.label}
          </span>
        </label>
      ))}
    </div>
  );
};

// Single Choice Mixed Question (Radio buttons with options in the question)
interface SingleChoiceMixedProps extends BaseQuestionProps {
  response: string | number | null;
  onResponseChange: (
    sectionId: string,
    questionId: string,
    value: string | number
  ) => void;
}

export const SingleChoiceMixedQuestion: React.FC<SingleChoiceMixedProps> = ({
  question,
  sectionId,
  response,
  onResponseChange,
}) => {
  console.log(
    `Rendering SingleChoiceMixedQuestion - question ID: ${question.id}`
  );
  console.log(`Options:`, question.options);
  console.log(`Current response:`, response);

  if (!question.options || question.options.length === 0) {
    return (
      <div className="rounded-md bg-red-50 p-3 text-red-600">
        Error: No options available for this question
      </div>
    );
  }

  return (
    <div className="space-y-2 md:space-y-3">
      {question.options.map((option) => {
        const isSelected = response === option.value;

        return (
          <label
            key={option.value}
            className={`flex cursor-pointer items-center rounded-lg border p-2 transition-colors duration-200 md:p-3 ${
              isSelected
                ? "border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-navy-900"
                : "border-gray-200 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
            }`}
          >
            <input
              type="radio"
              name={`${sectionId}_${question.id}`}
              value={option.value}
              checked={isSelected}
              onChange={() => {
                // const value = typeof option.value === 'string' && !isNaN(Number(option.value))
                //   ? Number(option.value)
                //   : option.value;
                const value = option.value;
                console.log(
                  `Selected option: ${option.label}, value: ${value}`
                );
                onResponseChange(sectionId, question.id, value);
              }}
              className="h-4 w-4 text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
            />
            <span
              className={`ml-2 text-sm md:ml-3 md:text-base ${
                isSelected
                  ? "font-medium text-brand-600 dark:text-brand-400"
                  : "text-gray-700 dark:text-gray-300"
              }`}
            >
              {option.label}
            </span>
          </label>
        );
      })}
    </div>
  );
};

// Multiple Selection Question Component
interface MultipleSelectionProps extends BaseQuestionProps {
  onMultipleResponseChange: (
    sectionId: string,
    questionId: string,
    optionValue: string,
    isChecked: boolean
  ) => void;
}

export const MultipleSelectionQuestion: React.FC<MultipleSelectionProps> = ({
  question,
  sectionId,
  response,
  onMultipleResponseChange,
}) => {
  return (
    <div className="space-y-2 md:space-y-3">
      {question.options?.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center rounded-lg border border-gray-200 p-2 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 md:p-3"
        >
          <input
            type="checkbox"
            checked={(response as string[])?.includes(option.value)}
            onChange={(e) =>
              onMultipleResponseChange(
                sectionId,
                question.id,
                option.value,
                e.target.checked
              )
            }
            className="h-4 w-4 rounded text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 md:ml-3 md:text-base">
            {option.label}
          </span>
        </label>
      ))}
    </div>
  );
};

// Text Input Question Component
interface TextInputProps extends BaseQuestionProps {
  onResponseChange: (
    sectionId: string,
    questionId: string,
    value: string
  ) => void;
}

export const TextInputQuestion: React.FC<TextInputProps> = ({
  question,
  sectionId,
  response,
  onResponseChange,
}) => {
  return (
    <div className="mt-2 md:mt-3">
      <input
        type="text"
        value={response as string}
        onChange={(e) =>
          onResponseChange(sectionId, question.id, e.target.value)
        }
        placeholder="Escriba su respuesta aquí..."
        className="w-full rounded-lg border border-gray-300 p-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:p-3 md:text-base"
      />
    </div>
  );
};

// Text Area Question Component
interface TextAreaProps extends BaseQuestionProps {
  onTextAreaChange: (
    sectionId: string,
    questionId: string,
    value: string
  ) => void;
}

export const TextAreaQuestion: React.FC<TextAreaProps> = ({
  question,
  sectionId,
  response,
  onTextAreaChange,
}) => {
  return (
    <div className="mt-2 md:mt-3">
      <textarea
        value={response as string}
        onChange={(e) =>
          onTextAreaChange(sectionId, question.id, e.target.value)
        }
        placeholder="Escriba su respuesta aquí..."
        className="min-h-[100px] w-full rounded-lg border border-gray-300 p-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:min-h-[120px] md:p-3 md:text-base"
        rows={5}
      />
      <div className="mt-1 text-right text-xs text-gray-500 md:mt-2">
        {response ? (response as string).length : 0} caracteres
      </div>
    </div>
  );
};

// Scale 1-10 Question Component
interface ScaleProps extends BaseQuestionProps {
  onScaleChange: (sectionId: string, questionId: string, value: number) => void;
}

export const ScaleQuestion: React.FC<ScaleProps> = ({
  question,
  sectionId,
  response,
  onScaleChange,
}) => {
  return (
    <>
      <div className="mb-3 md:mb-4">
        <input
          type="range"
          min="1"
          max="10"
          value={(response as number) || 0}
          onChange={(e) =>
            onScaleChange(sectionId, question.id, parseInt(e.target.value))
          }
          className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
        />

        <div className="mt-1 flex justify-between text-xs text-gray-500 md:mt-2">
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
          <span>5</span>
          <span>6</span>
          <span>7</span>
          <span>8</span>
          <span>9</span>
          <span>10</span>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-1 md:gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
          <button
            key={value}
            onClick={() => onScaleChange(sectionId, question.id, value)}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors md:h-10 md:w-10 md:text-base ${
              (response as number) === value
                ? "bg-brand-500 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      <div className="mt-3 flex justify-between text-xs font-medium md:mt-4 md:text-sm">
        <span className="text-gray-500">Muy bajo</span>
        <span className="text-gray-500">Muy alto</span>
      </div>
    </>
  );
};

// Multiple Options Question Component (for sections with response_type="multiple_options")
interface MultipleOptionsProps extends BaseQuestionProps {
  onMultipleResponseChange: (
    sectionId: string,
    questionId: string,
    optionValue: string,
    isChecked: boolean
  ) => void;
}

export const MultipleOptionsQuestion: React.FC<MultipleOptionsProps> = ({
  question,
  sectionId,
  response,
  onMultipleResponseChange,
}) => {
  return (
    <div className="space-y-2 md:space-y-3">
      {question.options?.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center rounded-lg border border-gray-200 p-2 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 md:p-3"
        >
          <input
            type="checkbox"
            value={option.value}
            checked={(response as string[]).includes(option.value)}
            onChange={(e) =>
              onMultipleResponseChange(
                sectionId,
                question.id,
                option.value,
                e.target.checked
              )
            }
            className="h-4 w-4 rounded text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 md:ml-3 md:text-base">
            {option.label}
          </span>
        </label>
      ))}
    </div>
  );
};

// Simple Checkbox Question Component (for sections with response_type="multiple_selection")
interface CheckboxQuestionProps extends BaseQuestionProps {
  onMultipleSelectionChange: (
    sectionId: string,
    questionId: string,
    checked: boolean
  ) => void;
}

export const CheckboxQuestion: React.FC<CheckboxQuestionProps> = ({
  question,
  sectionId,
  response,
  onMultipleSelectionChange,
}) => {
  console.log("Rendering checkbox question:", question);
  return (
    <div className="rounded-lg bg-gray-50 p-2 dark:bg-navy-800 md:p-3">
      <label className="flex cursor-pointer items-start gap-3">
        <div className="mt-0.5">
          <input
            type="checkbox"
            checked={!!response}
            onChange={(e) =>
              onMultipleSelectionChange(
                sectionId,
                question.id,
                e.target.checked
              )
            }
            className="h-4 w-4 rounded text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
          />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-white md:text-base">
            {question.text}
          </p>
          {question.options && question.options.length > 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 md:text-sm">
              {question.options[0].label}
            </p>
          )}
        </div>
      </label>
    </div>
  );
};

// Text List Question Component
interface TextListProps {
  sectionId: string;
  questions: QuestionnaireQuestion[];
  response: any;
  onResponseChange: (sectionId: string, questionId: string, value: any) => void;
  minItems: number;
  maxItems: number;
}

export const TextListQuestion: React.FC<TextListProps> = ({
  sectionId,
  questions,
  response,
  onResponseChange,
  minItems,
  maxItems,
}) => {
  const [items, setItems] = useState<
    Array<{
      caracteristica: string;
      es_por_tdah: boolean;
      soy_asi: boolean;
    }>
  >(() => {
    if (response && Array.isArray(response)) {
      return response;
    }
    return Array(minItems).fill({
      caracteristica: "",
      es_por_tdah: false,
      soy_asi: false,
    });
  });

  useEffect(() => {
    onResponseChange(sectionId, questions[0].id, items);
  }, [items, sectionId, questions, onResponseChange]);

  const handleAddItem = () => {
    if (items.length < maxItems) {
      setItems([
        ...items,
        { caracteristica: "", es_por_tdah: false, soy_asi: false },
      ]);
    }
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > minItems) {
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
    }
  };

  const handleItemChange = (
    index: number,
    field: "caracteristica" | "es_por_tdah" | "soy_asi",
    value: string | boolean
  ) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // If one radio is selected, unselect the other
    if (field === "es_por_tdah" && value === true) {
      newItems[index].soy_asi = false;
    } else if (field === "soy_asi" && value === true) {
      newItems[index].es_por_tdah = false;
    }

    setItems(newItems);
  };

  return (
    <div className="w-full">
      {/* Table Header */}
      <div className="mb-4 grid grid-cols-12 gap-4 rounded-t-xl bg-navy-700 p-4 dark:bg-navy-800">
        {questions.map((question) => (
          <div
            key={question.id}
            className={`${
              question.id === "caracteristica"
                ? "col-span-6"
                : "col-span-3 text-center"
            }`}
          >
            <span className="text-sm font-bold text-white">
              {question.text}
            </span>
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-12 gap-4 rounded-lg bg-white p-3 shadow-md dark:bg-navy-700"
          >
            <div className="col-span-6">
              <input
                type="text"
                value={item.caracteristica}
                onChange={(e) =>
                  handleItemChange(index, "caracteristica", e.target.value)
                }
                placeholder="Ingrese una característica..."
                className="dark:border-navy-450 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-navy-700 outline-none focus:border-brand-500 dark:bg-navy-700 dark:text-white dark:focus:border-white"
              />
            </div>
            <div className="col-span-3 flex items-center justify-center">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  checked={item.es_por_tdah}
                  onChange={(e) =>
                    handleItemChange(index, "es_por_tdah", e.target.checked)
                  }
                  className="peer sr-only"
                  name={`radio-group-${index}`}
                />
                <div className="dark:border-navy-450 peer h-5 w-5 rounded-full border-2 border-gray-300 bg-white transition-all hover:border-brand-500 peer-checked:border-brand-500 peer-checked:bg-brand-500 dark:bg-navy-700 dark:peer-checked:border-white dark:peer-checked:bg-white"></div>
              </label>
            </div>
            <div className="col-span-3 flex items-center justify-center">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="radio"
                  checked={item.soy_asi}
                  onChange={(e) =>
                    handleItemChange(index, "soy_asi", e.target.checked)
                  }
                  className="peer sr-only"
                  name={`radio-group-${index}`}
                />
                <div className="dark:border-navy-450 peer h-5 w-5 rounded-full border-2 border-gray-300 bg-white transition-all hover:border-brand-500 peer-checked:border-brand-500 peer-checked:bg-brand-500 dark:bg-navy-700 dark:peer-checked:border-white dark:peer-checked:bg-white"></div>
              </label>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Remove Buttons */}
      <div className="mt-4 flex justify-end space-x-2">
        {items.length > minItems && (
          <button
            onClick={() => handleRemoveItem(items.length - 1)}
            className="flex items-center rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-red-600"
          >
            <MdRemove className="mr-1" />
            Eliminar fila
          </button>
        )}
        {items.length < maxItems && (
          <button
            onClick={handleAddItem}
            className="flex items-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600"
          >
            <MdAdd className="mr-1" />
            Agregar fila
          </button>
        )}
      </div>
    </div>
  );
};

export default {
  SingleSelectionQuestion,
  MultipleSelectionQuestion,
  TextInputQuestion,
  TextAreaQuestion,
  ScaleQuestion,
  MultipleOptionsQuestion,
  CheckboxQuestion,
  TextListQuestion,
};
